import {
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Tooltip,
  Wrap,
  WrapItem,
  Button,
  Stack,
  Flex,
} from "@chakra-ui/react";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { providers } from "../Widgets/utils";
import InitialForm from "../Widgets/AddWidget/ConfigForms/InitailForm";
import LeadConnectorForm from "../Widgets/AddWidget/ConfigForms/LeadConnectorForm";
import LeadConnectorCallForm from "../Widgets/AddWidget/ConfigForms/LeadConnectorCallForm";
import SMSForm from "../Widgets/AddWidget/ConfigForms/SMSForm";
import WhatsAppForm from "../Widgets/AddWidget/ConfigForms/WhatsAppForm";
import PhoneForm from "../Widgets/AddWidget/ConfigForms/PhoneForm";
import HTMLCallForm from "../Widgets/AddWidget/ConfigForms/HTMLCallForm";
import TelnyxForm from "../Widgets/AddWidget/ConfigForms/TelnyxForm";
import PlivoForm from "../Widgets/AddWidget/ConfigForms/PlivoForm";
import FBMessengerForm from "../Widgets/AddWidget/ConfigForms/FBMessengerForm";
import InstagramForm from "../Widgets/AddWidget/ConfigForms/InstagramForm";
import TelegramForm from "../Widgets/AddWidget/ConfigForms/TelegramForm";
import useFinalSubmit from "../Widgets/AddWidget/useFinalSubmit";
import NameAndDescriptionForm from "../Assistants/NameAndDescriptionForm";
import { toast } from "react-toastify";
import { SettingContext } from "../Settings/Reducer/SettingProvider";
import SIPForm from "../Widgets/AddWidget/ConfigForms/SipForm";

const DeploymentModal = ({ isOpen, onClose, assistantData, fetch, agencyLogo, agencyName }) => {
  const { watch } = useFormContext();
  const assistantType = watch("assistant_type");
  const [step, setStep] = useState(0);
  const [selectedWidget, setSelectedWidget] = useState(null);
  const { state } = useContext(SettingContext);

  const widgetTypeMap = {
    simple: [
      "chat",
      "whatsapp",
      "leadconnector",
      "sms",
      "fb_messenger",
      "instagram",
      "telegram",
    ],
    phone: [
      "web_call",
      "html_call",
      "phone",
      "plivo_call",
      "telnyx_call",
      "leadconnector_call",
      "sip"
    ],
    realtime_openai: [
      "web_call",
      "html_call",
      "phone",
      "plivo_call",
      "telnyx_call",
      "leadconnector_call",
      "sip"
    ],
  };

  const configFormOptions = [
    {
      label: "Initial form",
      allowedWidgets: ["chat"],
      allowedAssistants: ["simple"],
      Children: InitialForm,
    },
    {
      label: "Lead connector form",
      allowedWidgets: ["leadconnector"],
      allowedAssistants: ["simple"],
      Children: LeadConnectorForm,
    },
    {
      label: "Lead connector cal form",
      allowedWidgets: ["leadconnector_call"],
      allowedAssistants: ["phone", "realtime_openai"],
      Children: LeadConnectorCallForm,
    },
    {
      label: "SMS form",
      allowedWidgets: ["sms"],
      allowedAssistants: ["simple"],
      Children: SMSForm,
    },
    {
      label: "WhatsApp form",
      allowedWidgets: ["whatsapp"],
      allowedAssistants: ["simple"],
      Children: WhatsAppForm,
    },
    {
      label: "Phone form",
      allowedWidgets: ["phone"],
      allowedAssistants: ["phone", "realtime_openai"],
      Children: PhoneForm,
    },
    {
      label: "HTML call form",
      allowedWidgets: ["html_call", "web_call"],
      allowedAssistants: ["phone", "realtime_openai"],
      Children: HTMLCallForm,
    },
    {
      label: "Telnyx form",
      allowedWidgets: ["telnyx_call"],
      allowedAssistants: ["phone", "realtime_openai"],
      Children: TelnyxForm,
    },
    {
      label: "Plivo form",
      allowedWidgets: ["plivo_call"],
      allowedAssistants: ["phone", "realtime_openai"],
      Children: PlivoForm,
    },
    {
      label: "FB messenger form",
      allowedWidgets: ["fb_messenger"],
      allowedAssistants: ["simple"],
      Children: FBMessengerForm,
    },
    {
      label: "Instagram form",
      allowedWidgets: ["instagram"],
      allowedAssistants: ["simple"],
      Children: InstagramForm,
    },
    {
      label: "Telegram form",
      allowedWidgets: ["telegram"],
      allowedAssistants: ["simple"],
      Children: TelegramForm,
    },
    {
      label: "Render form",
      allowedWidgets: ["render_form"],
      allowedAssistants: [""],
      Children: InitialForm,
    },
    {
      label: "Sip form",
      allowedWidgets: ["sip"],
      allowedAssistants: ["phone", "realtime_openai"],
      Children: SIPForm,
    },
  ];

  const widgetForm = useForm({
    defaultValues: {
      widget_type: "",
      assistant_id: null,
      assistant_type: "",
      twilio_auth_id: "",
      phone_number: "",
      max_call_duration: 600,
      form: null,
      recording: false,
      form_phone_number_field: "",
      sid: "",
    },
  });

  const { status, handleSubmit } = useFinalSubmit();

  const onSubmit = async (data) => {
    const provider = getProviderByWidgetValue(selectedWidget, providers);
    const { widget_name, widget_description, ...restData } = data;
    const finalValues = {
      ...restData,
      name: data.widget_name,
      description: data.widget_description,
      widget_type: selectedWidget,
      widget_provider: provider,
      provider,
      assistant_id: assistantData?.id,
      assistant_type: assistantType,
    };
    await handleSubmit(finalValues);
    setStep(0);
    setSelectedWidget(null);
    onClose();
    toast.success("Widget Created Successfully!");
    fetch();
  };

  function getProviderByWidgetValue(selectedValue, providers) {
    for (const [providerKey, providerData] of Object.entries(providers)) {
      const match = providerData.widgetTypes.find(
        (widget) => widget.value === selectedValue
      );
      if (match) {
        return providerKey;
      }
    }
    return null;
  }
  const relevantWidgetTypes = widgetTypeMap[assistantType] || [];
  const filteredProviders = useMemo(() => {
    const sipEnabled = state.credits?.sip_enabled === 1;
    return Object.fromEntries(
      Object.entries(providers).filter(([key]) => {
        if (key === "sip") return sipEnabled;
        return true;
      })
    );
  }, [state]);

  const filteredWidgetTypes = Object.entries(filteredProviders).flatMap(
    ([providerKey, provider]) =>
      provider.widgetTypes
        .filter((widget) => relevantWidgetTypes.includes(widget.value))
        .map((widget) => ({
          providerKey,
          value: widget.value,
          label: widget.label,
          icon: widget.icon,
          logo:
            providerKey === "insighto" && agencyLogo
              ? agencyLogo
              : provider.logo,
        })),
  );

  const selectedFormConfig = configFormOptions.find(
    (config) =>
      config.allowedWidgets.includes(selectedWidget) &&
      config.allowedAssistants.includes(assistantType)
  );

  useEffect(() => {
    widgetForm.reset({
      widget_type: "",
      assistant_id: null,
      assistant_type: "",
      twilio_auth_id: "",
      phone_number: "",
      max_call_duration: 600,
      form: null,
      recording: false,
      form_phone_number_field: "",
      sid: "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWidget]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        setStep(0);
        setSelectedWidget(null);
        onClose();
      }}
      size="3xl"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>Deploy {assistantData?.name} On:</ModalHeader>
        <ModalBody>
          {step === 0 && (
            <>
              <Stack spacing={6}>
                {Object.entries(
                  filteredWidgetTypes.reduce((acc, widget) => {
                    if (!acc[widget.providerKey]) {
                      acc[widget.providerKey] = {
                        logo: widget.logo,
                        label: providers[widget.providerKey].label,
                        widgets: [],
                      };
                    }
                    acc[widget.providerKey].widgets.push(widget);
                    return acc;
                  }, {})
                ).map(([providerKey, providerData]) => (
                  <Flex
                    key={providerKey}
                    align="center"
                    borderWidth="1px"
                    borderRadius="md"
                    p={2}
                  >
                    <Flex align="center" gap={3} mx={5} minW="120px" flex={2}>
                      <Image src={providerData.logo} boxSize="2rem" />
                      <Text fontWeight="semibold" textAlign="center">
                        {providerData.label === "System"
                          ? agencyName ? agencyName : "Insighto.Ai"
                          : providerData.label}
                      </Text>
                    </Flex>

                    <Wrap spacing={3} flex={5}>
                      {providerData.widgets.map((widget) => (
                        <WrapItem key={widget.value}>
                          <Tooltip label={widget.label} hasArrow>
                            <IconButton
                              size="lg"
                              aria-label={widget.label}
                              onClick={() => setSelectedWidget(widget.value)}
                              icon={
                                widget.icon?.props ? (
                                  widget.icon
                                ) : (
                                  <Image src={widget.logo} boxSize="1.5rem" />
                                )
                              }
                              colorScheme={
                                selectedWidget === widget.value
                                  ? "blue"
                                  : "gray"
                              }
                            />
                          </Tooltip>
                        </WrapItem>
                      ))}
                    </Wrap>
                  </Flex>
                ))}
              </Stack>
            </>
          )}

          <FormProvider {...widgetForm}>
            {step === 1 && (
              <NameAndDescriptionForm
                widgetForm={widgetForm}
                onNext={() => setStep(2)}
                onBack={() => setStep(0)}
              />
            )}

            {step === 2 && (
              <>
                {selectedFormConfig ? (
                  <selectedFormConfig.Children
                    key={selectedWidget}
                    widgetForm={widgetForm}
                    onSubmit={onSubmit}
                    loadingStatus={status}
                    onBack={() => setStep(1)}
                  />
                ) : (
                  <Text color="red.500">
                    No form available for selected widget and assistant type.
                  </Text>
                )}
              </>
            )}
          </FormProvider>
        </ModalBody>

        {step === 0 && (
          <ModalFooter gap={3}>
            <Button
              onClick={() => setStep((prev) => Math.max(prev - 1, 0))}
              isDisabled={step === 0}
            >
              Back
            </Button>
            <Button
              colorScheme="blue"
              isDisabled={step === 0 && !selectedWidget}
              onClick={() => {
                if (step === 2) {
                  onClose();
                  setStep(0);
                  setSelectedWidget(null);
                } else {
                  setStep((prev) => prev + 1);
                }
              }}
            >
              {step === 2 ? "Finish" : "Next"}
            </Button>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
};

export default DeploymentModal;
